import { useRef, useState, useCallback } from 'react';
import { Button } from '@safetyhub/ui';
import { SignaturePadWithRef } from '../SignaturePad';
import type { SignaturePadRef } from '../SignaturePad';
import Modal from '../Modal';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface ApprovalSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  approvalLevel: number;
  defaultSignerName?: string;
  defaultSignerTitle?: string;
  /**
   * 위임 보고서에서 **내가 쓴 반려 사유가 담당자에게 그대로 나가는 경우**에만 준다 (S49).
   *
   * 판정(`relaysComment`)은 서버가 발송을 끊는 것과 **같은 술어**로 계산해 내려준다 —
   * 화면이 "위임이면 무조건"으로 다시 판정하면 제3자 반려에서 거짓 안내가 된다(그 글은
   * 담당자에게 나가지 않는다). 비위임 보고서는 이 prop 없이 문구가 변하지 않는다(대조군).
   */
  relayedContactName?: string | null;
  onSubmit: (params: {
    action: 'APPROVE' | 'REJECT';
    signerName: string;
    signerTitle?: string;
    comment?: string;
    signatureData?: string;
  }) => void | Promise<void>;
}

export function ApprovalSignModal({
  isOpen,
  onClose,
  approvalLevel,
  defaultSignerName = '',
  defaultSignerTitle = '',
  relayedContactName,
  onSubmit,
}: ApprovalSignModalProps) {
  // 이름이 들어온 것 자체가 "이 글이 그 사람에게 그대로 전달된다"는 뜻이다(판정은 서버가 한다).
  const relaysComment = typeof relayedContactName === 'string' && relayedContactName.length > 0;
  const [action, setAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [signerTitle, setSignerTitle] = useState(defaultSignerTitle);
  const [comment, setComment] = useState('');
  const [imageError, setImageError] = useState<string | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageLoad = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError(null);

    if (!file.type.startsWith('image/')) {
      setImageError('이미지 파일만 업로드할 수 있습니다');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImageError('파일 크기는 5MB 이하여야 합니다');
      e.target.value = '';
      return;
    }

    try {
      await signaturePadRef.current?.loadImage(file);
    } catch {
      setImageError('이미지를 불러오는 데 실패했습니다');
    } finally {
      e.target.value = '';
    }
  }, []);

  const handleClose = () => {
    setAction(null);
    setComment('');
    setImageError(null);
    setHasSignature(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    signaturePadRef.current?.clear();
    onClose();
  };

  const handleSubmit = async () => {
    if (!action || !signerName.trim()) return;

    if (action === 'REJECT' && !comment.trim()) {
      alert('반려 사유를 입력해주세요.');
      return;
    }

    let signatureData: string | undefined;
    if (action === 'APPROVE') {
      if (signaturePadRef.current?.isEmpty()) {
        alert('서명을 입력해주세요.');
        return;
      }
      signatureData = signaturePadRef.current?.toDataURL();
    }

    await onSubmit({
      action,
      signerName: signerName.trim(),
      signerTitle: signerTitle.trim() || undefined,
      comment: comment.trim() || undefined,
      signatureData,
    });
  };

  const isDisabled =
    !action ||
    !signerName.trim() ||
    (action === 'REJECT' && !comment.trim()) ||
    (action === 'APPROVE' && !hasSignature);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="결재 서명"
      description={`${approvalLevel}단계 결재`}
      size="md"
    >
      <div className="space-y-3">
        {/* 결재 유형 선택 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1.5">
            결재 유형 <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-3">
            <label
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border-2 rounded-card cursor-pointer transition-colors ${
                action === 'APPROVE'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <input
                type="radio"
                name="approval-action"
                value="APPROVE"
                checked={action === 'APPROVE'}
                onChange={() => setAction('APPROVE')}
                className="sr-only"
              />
              <span>&#10003;</span>
              <span className="font-medium">승인</span>
            </label>
            <label
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border-2 rounded-card cursor-pointer transition-colors ${
                action === 'REJECT'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <input
                type="radio"
                name="approval-action"
                value="REJECT"
                checked={action === 'REJECT'}
                onChange={() => setAction('REJECT')}
                className="sr-only"
              />
              <span>&#9888;</span>
              <span className="font-medium">반려</span>
            </label>
          </div>
        </div>

        {/* 결재자 정보 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              결재자 이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="이름 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">직책</label>
            <input
              type="text"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="직책 입력 (선택)"
            />
          </div>
        </div>

        {/* 결재 의견 / 반려 사유 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            {action === 'REJECT' ? '반려 사유' : '결재 의견'}
            {action === 'REJECT' && <span className="text-red-500"> *</span>}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={action === 'REJECT' ? (relaysComment ? 3 : 2) : 1}
            className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
            placeholder={
              action === 'REJECT'
                ? relaysComment
                  ? '반려 사유를 입력해주세요 — 이 내용이 그대로 발송됩니다'
                  : '반려 사유를 입력해주세요'
                : '결재 의견을 입력하세요 (선택)'
            }
          />
          {/*
            S49 — 위임 보고서에서 **내 글이 그대로 외부인에게 나가는** 경우에만 알린다.
            이 정보가 화면에 없는 것이 "구체적이지 않은 반려"의 실제 원인이었다: 작성자는
            자기가 쓰는 글이 문자로 나간다는 걸 모른 채 내부 메모처럼 썼다.
          */}
          {action === 'REJECT' && relaysComment && (
            <p
              data-testid="rejection-relay-notice"
              className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-control px-2 py-1.5"
            >
              이 보고서는 {relayedContactName}님에게 위임된 문서입니다. 반려 사유가 수정 요청
              문자로 그대로 전달되니, 무엇을 어떻게 고쳐야 하는지 적어 주세요.
            </p>
          )}
        </div>

        {/* 서명 (승인 시에만 표시) */}
        {action !== 'REJECT' && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">
              서명 <span className="text-red-500">*</span>
            </label>
            <SignaturePadWithRef ref={signaturePadRef} height={120} onSignatureChange={setHasSignature} />
            <div className="mt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                aria-label="서명 이미지 파일 선택"
                onChange={handleImageLoad}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-ink-600 hover:text-ink-900 underline"
              >
                이미지 불러오기
              </button>
              {imageError && <p className="mt-1 text-xs text-red-600">{imageError}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <Button
          variant={action === 'REJECT' ? 'danger' : 'success'}
          size="lg"
          onClick={handleSubmit}
          disabled={isDisabled}
          className="w-full"
        >
          {action === 'REJECT'
            ? '반려하기'
            : action === 'APPROVE'
              ? '승인하기'
              : '결재 유형을 선택하세요'}
        </Button>
      </div>
    </Modal>
  );
}
